import React from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import { PRODUCTS, getProductBySlug } from "@/constants/products";
import { PRODUCT_FAQS } from "@/constants/faqs";

const PRICE_RANGES = {
  "passenger-elevators": { low: "800000", high: "2000000" },
  "home-lifts": { low: "550000", high: "1200000" },
  "freight-elevators": { low: "1000000", high: "2500000" },
  "hospital-lifts": { low: "1300000", high: "2200000" },
  escalators: { low: "3500000", high: "8000000" },
  "moving-walkways": { low: "4000000", high: "9000000" },
};

export default function ProductDetailPage({ slug }) {
  const product = getProductBySlug(slug);
  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3);

  return (
    <>
      <SEO
        title={product.seoTitle}
        description={product.seoDesc}
        path={`/products/${product.slug}`}
        keywords={product.keywords}
        image={product.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          image: product.image,
          description: product.long,
          brand: { "@type": "Brand", name: "Vila Elevators & Escalators" },
          manufacturer: {
            "@type": "Organization",
            name: "Vila Elevators & Escalators",
            url: "https://vilaelevators.in",
          },
          offers: {
            "@type": "AggregateOffer",
            lowPrice: PRICE_RANGES[product.slug].low,
            highPrice: PRICE_RANGES[product.slug].high,
            priceCurrency: "INR",
            offerCount: "4",
            availability: "https://schema.org/InStock",
            seller: { "@type": "Organization", name: "Vila Elevators & Escalators" },
          },
        }}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 border-b border-[#e5ddd0] overflow-hidden">
        <img src={product.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf7f2] via-[#faf7f2]/90 to-[#faf7f2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-8">
          <div className="flex items-center gap-2 text-xs text-[#8a8a8a] mb-6">
            <Link to="/" className="hover:text-[#b08d57]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-[#b08d57]">Products</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#4a4a4a]">{product.title}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-block bg-[#b08d57] text-white text-[10px] uppercase font-bold tracking-widest px-3 py-1 mb-6">
                {product.tag}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-[#1a1a1a] leading-[0.95] mb-6">
                {product.title}
              </h1>
              <p className="text-[#b08d57] text-lg font-medium mb-6">{product.tagline}</p>
              <p className="text-[#6b6b6b] text-lg leading-relaxed mb-8">{product.long}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#enquiry" data-testid={`product-cta-quote-${product.slug}`} className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center justify-center gap-2">
                  Request Quote <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/contact" className="border-2 border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center justify-center gap-2">
                  Site Survey
                </Link>
              </div>
            </motion.div>
            <motion.img
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              src={product.image}
              alt={product.title}
              className="w-full aspect-[4/3] object-cover border border-[#e5ddd0]"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Key Features</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-8">
              Engineered for {product.title.toLowerCase()}.
            </h2>
            <ul className="space-y-4">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[#4a4a4a]">
                  <CheckCircle className="w-5 h-5 text-[#b08d57] flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Technical Specifications</div>
            <h3 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-8">
              Specs at a glance
            </h3>
            <dl className="border border-[#e5ddd0]">
              {Object.entries(product.specs).map(([k, v], i) => (
                <div key={k} className={`grid grid-cols-2 ${i > 0 ? "border-t border-[#e5ddd0]" : ""}`}>
                  <dt className="p-5 text-[#6b6b6b] text-sm bg-[#faf7f2]/50 uppercase tracking-wider text-xs font-bold">{k}</dt>
                  <dd className="p-5 text-[#1a1a1a] text-sm">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Ideal For</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-10">Where {product.title.toLowerCase()} fit best</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {product.useCases.map((u) => (
              <div key={u} className="border border-[#e5ddd0] bg-[#faf7f2] p-6 hover:border-[#b08d57]/50 transition-colors">
                <div className="text-[#b08d57] mb-3">▲</div>
                <div className="text-[#1a1a1a] font-display font-semibold">{u}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section id="enquiry" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Get a Quote</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6">
              Request pricing for {product.title.toLowerCase()}.
            </h2>
            <p className="text-[#6b6b6b] text-lg leading-relaxed">
              Share your building dimensions, floor count and load requirements — we'll respond with a tailored proposal within one business day.
            </p>
          </div>
          <div className="border border-[#e5ddd0] bg-[#faf7f2]/50 p-8 lg:p-10">
            <ContactForm sourcePage={`product-${product.slug}`} />
          </div>
        </div>
      </section>

      {/* FAQ — AEO */}
      <FAQSection
        faqs={PRODUCT_FAQS[product.slug]}
        title={`${product.title} — common questions`}
      />

      {/* Related */}
      <section className="py-20 bg-[#faf7f2]/40 border-t border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Also See</div>
          <h3 className="font-display text-3xl font-bold text-[#1a1a1a] mb-10">Related products</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/products/${r.slug}`}
                data-testid={`related-product-${r.slug}`}
                className="group border border-[#e5ddd0] hover:border-[#b08d57]/50 bg-[#faf7f2] overflow-hidden transition-colors"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="font-display text-xl font-bold text-[#1a1a1a] mb-2">{r.title}</div>
                  <div className="text-[#6b6b6b] text-sm">{r.short.slice(0, 90)}…</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
